/**
 * Use Issue Workflow Hook
 *
 * Unified hook that conditionally uses revision tracking internally,
 * eliminating duplicate state computation from calling both hooks.
 *
 * Architecture:
 * - When tracking enabled: Delegates to useRevisionTracking
 * - When tracking disabled: Delegates to useRevisedText
 * - Single execution path prevents duplicate state computation
 */

import { useMemo } from 'react';
import type { InlineIssue, IssueStatus, ViewMode, RevisionTrackingConfig } from '../types';
import type { UserTier } from '@/lib/analytics/events';
import { useRevisionTracking } from './useRevisionTracking';

interface UseIssueWorkflowOptions {
  /** Original message text */
  message: string;
  /** Initial issues array */
  initialIssues: InlineIssue[];
  /** Optional revision tracking configuration */
  tracking?: RevisionTrackingConfig;
  /** User's subscription tier for analytics */
  tier?: UserTier;
}

interface UseIssueWorkflowReturn {
  /** Current issues with status */
  issues: InlineIssue[];
  /** Current view mode */
  viewMode: ViewMode;
  /** Revised text with accepted replacements */
  revisedText: string;
  /** Whether there are any changes */
  hasChanges: boolean;
  /** Count of issues by status */
  statusCounts: Record<IssueStatus, number>;
  /** Set view mode */
  setViewMode: (mode: ViewMode) => void;
  /** Update issue status */
  updateIssueStatus: (issueId: number, status: IssueStatus) => void;
  /** Accept all pending issues */
  acceptAll: () => void;
  /** Reset all issues to pending */
  resetAll: () => void;
  /** Accept an issue with user-modified text (fires rewrite_modified event + DB write) */
  modifyAndAcceptIssue: (issueId: number, modifiedText: string) => void;
  /** Revision session ID (null if tracking disabled or not created yet) */
  revisionId: string | null;
  /** Whether tracking is enabled and in progress */
  isTrackingEnabled: boolean;
  /** Original drama score (0 if tracking disabled) */
  originalScore: number;
  /** Calculated revised score (0 if tracking disabled) */
  revisedScore: number;
  /** Score improvement delta (0 if tracking disabled) */
  scoreDelta: number;
  /** Whether a server-side rescore is in progress */
  isRescoring: boolean;
}

/**
 * Hook for managing issue workflow with optional revision tracking
 *
 * Conditionally delegates to useRevisionTracking (when enabled) or
 * useRevisedText (when disabled) to prevent duplicate computation.
 */
export function useIssueWorkflow({
  message,
  initialIssues,
  tracking,
  tier = 'free',
}: UseIssueWorkflowOptions): UseIssueWorkflowReturn {
  // Determine if tracking is enabled
  const isTrackingEnabled = tracking?.enabled !== false && !!tracking?.analysisId;

  // Always use useRevisionTracking - it wraps useRevisedText internally
  // and handles enabled/disabled state via the 'enabled' parameter
  const result = useRevisionTracking({
    message,
    initialIssues,
    // 'disabled' sentinel distinguishes "no tracking config" from empty string
    analysisId: tracking?.analysisId ?? 'disabled',
    originalScore: tracking?.originalScore ?? 0,
    originalSummary: tracking?.originalSummary,
    agentExecutionId: tracking?.agentExecutionId,
    modelId: tracking?.modelId,
    enabled: isTrackingEnabled,
    tier,
    onTrackingError: tracking?.onTrackingError,
  });

  // Memoize the return object to prevent unnecessary re-renders in consumers.
  // Only re-creates when the underlying result values actually change.
  return useMemo((): UseIssueWorkflowReturn => ({
    issues: result.issues,
    viewMode: result.viewMode,
    revisedText: result.revisedText,
    hasChanges: result.hasChanges,
    statusCounts: result.statusCounts,
    setViewMode: result.setViewMode,
    updateIssueStatus: result.updateIssueStatus,
    acceptAll: result.acceptAll,
    resetAll: result.resetAll,
    modifyAndAcceptIssue: result.modifyAndAcceptIssue,
    revisionId: result.revisionId,
    isTrackingEnabled: result.isTracking,
    originalScore: result.originalScore,
    revisedScore: result.revisedScore,
    scoreDelta: result.scoreDelta,
    isRescoring: result.isRescoring ?? false,
  }), [
    result.issues,
    result.viewMode,
    result.revisedText,
    result.hasChanges,
    result.statusCounts,
    result.setViewMode,
    result.updateIssueStatus,
    result.acceptAll,
    result.resetAll,
    result.modifyAndAcceptIssue,
    result.revisionId,
    result.isTracking,
    result.originalScore,
    result.revisedScore,
    result.scoreDelta,
    result.isRescoring,
  ]);
}

export default useIssueWorkflow;
